import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { MatSelect } from "@angular/material/select";
import { UIChart } from "primeng/chart";
import { Subscription } from "rxjs";
import { UserService } from "src/app/session/services/user.service";
import { Account } from "src/app/settings/models/account";
import { OperationType } from "src/app/settings/models/operation-type";
import { AccountService } from "src/app/settings/services/account.service";
import { OperationTypeService } from "src/app/settings/services/operation-type.service";

import {
  COTECNA_USER_KEY,
  DASHBOARD_INSPECTIONS_TAB,
  DASHBOARD_INSPECTORS_TAB,
  GLOBAL_MANAGER_ROLE,
  MANAGER_ROLE,
} from "../constants/constants";
import { ReportingService } from "../services/reporting.service";

@Component({
  selector: "im-dashboards",
  templateUrl: "./dashboards.component.html",
  styleUrls: ["./dashboards.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DashboardsComponent implements OnInit, OnDestroy {
  private monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  public accounts: Account[] = [
    {
      id: "0",
      name: "All",
    },
  ];

  public operationTypes: OperationType[] = [
    {
      id: "0",
      name: "All",
    },
  ];
  public accountSelected: Account;
  public operationTypeSelected: OperationType;
  public dateFrom = this.createDateFrom();
  public dateTo = new Date();
  public selectedTab = 0;
  public isFilterDisabled: boolean = true;
  public data: any;
  public options: any;
  public showChart = false;
  public dataLoaded = false;

  @ViewChild("chart") public chart: UIChart;
  @ViewChild(MatSelect) public matSelect: MatSelect;

  private timeInterval: Subscription;
  public label: string;

  public isManager: boolean = false;
  public constructor(
    private accountsService: AccountService,
    private userService: UserService,
    private reportingService: ReportingService,
    private operationTypeService: OperationTypeService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.accountSelected = this.accounts[0];
    const months = this.diff(this.dateFrom, this.dateTo);
    this.data = {
      labels: months,
      datasets: [],
    };

    this.options = {
      responsive: true,
      legend: {
        display: false,
      },
      elements: {
        line: {
          tension: 0,
        },
      },
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
            },
          },
        ],
      },
    };
  }

  public ngOnDestroy(): void {
    this.timeInterval?.unsubscribe();
  }

  public async ngOnInit() {
    await this.manageAccounts();
    await this.manageOperationTypes();
    await this.refreshChart();
    this.changeDetectorRef.detectChanges();
  }

  public async onAccountSelected(event: string) {
    this.operationTypes = [{ id: "0", name: "All" }];
    this.accountSelected = this.accounts.find((acc) => acc.id === event);
    if (event === "0") {
      this.manageOperationTypes();
    } else {
      const operationTypes =
        await this.operationTypeService.getAccountReportingOptions(event);
      this.operationTypes = this.operationTypes.concat(
        operationTypes.sort(this.sortOperationTypes()),
      );
      this.operationTypeSelected = this.operationTypes[0];
    }
  }

  public onOperationTypeSelected(event: string) {
    this.operationTypeSelected = this.operationTypes.find(
      (ot) => ot.id === event,
    );
  }

  public onDateFromChange(event: any) {
    this.dateFrom = event;
    const fromDate = this.dateFrom ?? this.createDateFrom();
    this.data.labels = this.diff(fromDate, this.dateTo);
    this.isFilterDisabled = this.checkDateDifference(
      this.dateFrom,
      this.dateTo,
    );
  }

  public onDateToChange(event: any) {
    this.dateTo = event;
    const toDate = this.dateTo ?? new Date();
    this.data.labels = this.diff(this.dateFrom, toDate);
    this.isFilterDisabled = this.checkDateDifference(
      this.dateFrom,
      this.dateTo,
    );
  }

  public onTabSelected(event: any) {
    this.showChart = false;
    this.selectedTab = event.index;
  }

  public async onGoButton() {
    await this.refreshChart();
  }

  public async onExportButton() {
    try {
      this.dataLoaded = false;
      await this.reportingService.exportReportData(
        this.dateFrom,
        this.dateTo,
        this.accountSelected.id == "0" ? null : this.accountSelected.id,
        this.operationTypeSelected.id == "0"
          ? null
          : this.operationTypeSelected.id,
      );
    } finally {
      this.dataLoaded = true;
    }
  }

  private diff(from, to) {
    const months = [];
    const fromYear = from.getFullYear();
    const toYear = to.getFullYear();
    const diffYear = 12 * (toYear - fromYear) + to.getMonth();

    for (let i = from.getMonth(); i <= diffYear; i++) {
      months.push(
        this.monthNames[i % 12] +
          " " +
          Math.floor(fromYear + i / 12)
            .toString()
            .substr(-2),
      );
    }
    return months;
  }

  private async refreshChart() {
    this.showChart = false;
    try {
      await this.getData();
      this.showChart = true;
    } catch {
      this.showChart = false;
    } finally {
      this.changeDetectorRef.detectChanges();
    }
  }

  private async getData() {
    this.dateFrom = this.dateFrom ?? new Date(this.createDateFrom());
    this.dateTo = this.dateTo ?? new Date();

    this.dataLoaded = false;
    let data = [];
    try {
      switch (this.selectedTab) {
        case 0: //"Inspections"
          this.label = DASHBOARD_INSPECTIONS_TAB;
          data = await this.reportingService.getInspectionsData(
            this.dateFrom,
            this.dateTo,
            this.accountSelected.id == "0" ? null : this.accountSelected.id,
            this.operationTypeSelected.id == "0"
              ? null
              : this.operationTypeSelected.id,
          );
          break;
        case 1: //"Inspectors"
          this.label = DASHBOARD_INSPECTORS_TAB;
          data = await this.reportingService.getInspectorsData(
            this.dateFrom,
            this.dateTo,
            this.accountSelected.id == "0" ? null : this.accountSelected.id,
            this.operationTypeSelected.id == "0"
              ? null
              : this.operationTypeSelected.name,
          );
          break;
      }
      this.data = {
        ...this.data,
        datasets: [
          {
            label: this.label,
            data,
            fill: false,
            borderColor: "#080484",
          },
        ],
      };
      this.dataLoaded = true;
    } finally {
      this.dataLoaded = true;
    }
  }

  private async manageAccounts() {
    const existingUser = JSON.parse(
      globalThis.sessionStorage.getItem(COTECNA_USER_KEY),
    );
    const user = existingUser ?? (await this.userService.getCurrentUser());

    if (user.roles.includes(GLOBAL_MANAGER_ROLE)) {
      const accounts = await this.accountsService.getAccounts();
      accounts.sort(this.sortAccounts());
      this.accounts = this.accounts.concat(accounts);
    } else {
      this.addUserAccount(user.accountId, user.accountName);
      this.accounts = this.accounts.filter((a) => a.id == user.accountId);
    }

    if (
      user.roles.includes(GLOBAL_MANAGER_ROLE) ||
      user.roles.includes(MANAGER_ROLE)
    ) {
      this.isManager = true;
    }

    this.accountSelected = this.accounts[0];
  }

  private addUserAccount(accountId: string, accountName: string): void {
    const userAccount: Account = {
      id: accountId,
      name: accountName,
    };

    this.accounts.push(userAccount);
  }

  private async manageOperationTypes() {
    const operationTypes =
      await this.operationTypeService.getReportingOptions();
    this.operationTypes = this.operationTypes.concat(
      operationTypes.sort(this.sortOperationTypes()),
    );
    this.operationTypeSelected = this.operationTypes[0];
  }

  private sortOperationTypes(): (a: OperationType, b: OperationType) => number {
    return (a1, a2) => {
      if (a1.name > a2.name) {
        return 1;
      }

      if (a1.name < a2.name) {
        return -1;
      }

      return 0;
    };
  }

  private sortAccounts(): (a: Account, b: Account) => number {
    return (a1, a2) => {
      if (a1.name > a2.name) {
        return 1;
      }

      if (a1.name < a2.name) {
        return -1;
      }

      return 0;
    };
  }

  private createDateFrom(): Date {
    return new Date(new Date().setMonth(new Date().getMonth() - 1));
  }

  private checkDateDifference(dateFrom: Date, dateTo: Date): boolean {
    const diffTime = Math.abs(dateTo.getTime() - dateFrom.getTime());
    const diffYears = diffTime / (1000 * 60 * 60 * 24 * 365.25);

    if (diffYears > 1) {
      return false;
    } else {
      return true;
    }
  }
}
